import * as React from 'react';
import { connect } from 'react-redux';

import '../Main.scss';
import '../ImportCustomerFinancials/NewConnectionModal.scss';
import './DestinationStep.scss';

import { Button } from '../design-system/components';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faSpinnerThird } from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

import { PortalTextCommon } from '../../Models/Api/strongbox.financialportal';
import {
    FieldGuideListCompaniesResult,
    ListFieldGuideCompanies,
    isFieldGuideListCompaniesFailure,
} from '../../Services/FieldGuideService';
import { ApplicationState } from '../../Store';
import {
    FieldGuideCompanySelection,
    GetFieldGuideSelectedCompany,
    actionCreators as FieldGuideStoreActions,
} from '../../Store/FieldGuide';
import { GetPortalText } from '../../Store/PortalAssets';

import { DestinationCard } from './DestinationCard';
import { DisabledSearchBox } from './DisabledSearchBox';

type InjectedReduxState = {
    selectedCompany: FieldGuideCompanySelection | undefined;
    commonText: PortalTextCommon | undefined;
};

type InjectedActionCreators = {
    SelectCompany: (company: FieldGuideCompanySelection) => void;
    NextUploadStep: () => void;
};

type CompanyStepProps = {
    onCancel: () => void;
    // Overridable so tests need not stub the network; defaults to the real service.
    listCompanies?: (options?: { signal?: AbortSignal }) => Promise<FieldGuideListCompaniesResult>;
};

type Props = CompanyStepProps & InjectedReduxState & InjectedActionCreators;

type CompanyList =
    | { status: 'loading' }
    | { status: 'loaded'; companies: readonly FieldGuideCompanySelection[] }
    // message is the server's own message, when it sent one; otherwise the generic portal text is shown.
    | { status: 'failed'; message?: string };

export const CompanyStepComponent: React.FC<Props> = (props): React.ReactElement => {
    const { onCancel, listCompanies = ListFieldGuideCompanies, selectedCompany, commonText, SelectCompany, NextUploadStep } = props;

    // One-shot, like the cards: a second click on Next (or on a card after Next) must not dispatch a second advance.
    const advanced = React.useRef(false);
    const advance = (): void => {
        if (advanced.current) {
            return;
        }
        advanced.current = true;
        NextUploadStep();
    };

    const [list, setList] = React.useState<CompanyList>({ status: 'loading' });

    React.useEffect(() => {
        const controller = new AbortController();

        const load = async (): Promise<void> => {
            try {
                const result = await listCompanies({ signal: controller.signal });
                if (controller.signal.aborted) {
                    return;
                }

                setList(isFieldGuideListCompaniesFailure(result)
                    ? { status: 'failed', message: result.message }
                    : { status: 'loaded', companies: result.response.items });
            } catch {
                if (!controller.signal.aborted) {
                    setList({ status: 'failed' });
                }
            }
        };
        void load();

        return () => controller.abort();
        // Mount-only: loads the company list once per step entry.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const renderList = (): React.ReactNode => {
        if (list.status === 'loading') {
            return (
                <div className={'page-centered-content'} role={'status'} aria-label={(commonText?.fieldGuideLoadingClients || 'Loading clients')}>
                    <FontAwesomeIcon icon={faSpinnerThird} spin className={'page-large-icon'} />
                </div>
            );
        }

        if (list.status === 'failed') {
            return <p className={'page-body-text'} role={'alert'}>{list.message || commonText?.fieldGuideLoadClientsFailed || 'We could not load your Fieldguide clients. Please try again, and contact support if the problem continues.'}</p>;
        }

        if (list.companies.length === 0) {
            return <p className={'page-body-text'}>{commonText?.fieldGuideNoClients || 'No clients found in your Fieldguide account'}</p>;
        }

        return (
            <div className={'page-stack page-stack--fill'}>
                <DisabledSearchBox
                    placeholder={commonText?.fieldGuideSearchClients || 'Search clients'}
                    hoverText={commonText?.fieldGuideSearchComingSoon || 'Search is coming in story #41109.'}
                />
                <div className={'page-stack page-stack--small page-scroll-list'}>
                    {list.companies.map(company => (
                        <DestinationCard
                            key={company.id}
                            icon={faBuilding}
                            heading={company.name}
                            selected={selectedCompany?.id === company.id}
                            onSelect={() => {
                                SelectCompany({ id: company.id, name: company.name });
                                advance();
                            }}
                        />
                    ))}
                </div>
            </div>
        );
    };

    const canAdvance = list.status === 'loaded' && selectedCompany !== undefined
        && list.companies.some(company => company.id === selectedCompany.id);

    return (
        <div className={'field-guide-step'}>
            <div className={'field-guide-step__content page-stack'}>
                <p className={'page-body-text'}>{commonText?.fieldGuideSelectClientIntro || 'Choose the Fieldguide client this collection belongs to.'}</p>
                {renderList()}
            </div>
            <div className={'field-guide-footer'}>
                <Button type={'floating'} onClick={onCancel}>{commonText?.cancel || 'Cancel'}</Button>
                <div className={'cr-modal-footer__button-group'}>
                    <Button type={'primary'} className={'page-btn--min-width'} disabled={!canAdvance} onClick={advance}>{commonText?.nextButton || 'Next'}</Button>
                </div>
            </div>
        </div>
    );
};

export const CompanyStepContainer = connect<InjectedReduxState, InjectedActionCreators, CompanyStepProps, ApplicationState>(
    (appState: ApplicationState) => {
        const result: InjectedReduxState = {
            selectedCompany: GetFieldGuideSelectedCompany(appState),
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
        };
        return result;
    },
    {
        SelectCompany: FieldGuideStoreActions.SelectCompany,
        NextUploadStep: FieldGuideStoreActions.NextUploadStep,
    }
)(CompanyStepComponent);
